/**
 * i18n helper for pricing/plans translations
 * Provides locale-aware fallbacks to prevent RO/EN mixing
 */

type Locale = 'ro' | 'en';

interface FallbackMap {
  [key: string]: {
    ro: string;
    en: string;
  };
}

const fallbacks: FallbackMap = {
  'landing.pricing.title': {
    ro: 'Cât costă Jubiel?',
    en: 'How much does Jubiel cost?',
  },
  'pricing.title': {
    ro: 'Planuri și prețuri',
    en: 'Plans and pricing',
  },
  'pricing.standard.popular': {
    ro: 'Cel mai popular',
    en: 'Most popular',
  },
  'landing.pricing.standard.popular': {
    ro: 'Cel mai popular',
    en: 'Most popular',
  },
  'payment.title': {
    ro: 'Finalizează abonamentul',
    en: 'Complete Your Subscription',
  },
  'payment.subtitle': {
    ro: 'Alege planul și finalizează plata pentru a începe',
    en: 'Choose your plan and complete payment to get started',
  },
  'payment.selectPlan': {
    ro: 'Selectează planul',
    en: 'Select Plan',
  },
  'payment.submitCta': {
    ro: 'Completează plata',
    en: 'Complete payment',
  },
  'payment.activateFreePlan': {
    ro: 'Activează planul gratuit',
    en: 'Activate free plan',
  },
  'payment.error': {
    ro: 'Ceva nu a mers bine. Te rugăm să încerci din nou.',
    en: 'Something went wrong. Please try again.',
  },
  'payment.intentError': {
    ro: 'Nu s-a putut inițializa plata. Te rugăm să încerci din nou.',
    en: 'Unable to initialize payment. Please try again.',
  },
  'payment.freeActivated': {
    ro: 'Planul gratuit a fost activat!',
    en: 'Free plan activated!',
  },
  'payment.stripeNotReady': {
    ro: 'Plata nu este pregătită încă. Te rugăm să încerci într-un moment.',
    en: 'Payment is not ready yet. Please try again in a moment.',
  },
  'payment.cardUnavailable': {
    ro: 'Câmpul pentru card nu este disponibil. Te rugăm să reîmprospătezi pagina și să încerci din nou.',
    en: 'Card input is unavailable. Please refresh and try again.',
  },
  'payment.success': {
    ro: 'Plata a reușit!',
    en: 'Payment successful!',
  },
  'payment.testSuccess': {
    ro: 'Plata de test a fost confirmată!',
    en: 'Test payment confirmed!',
  },
  'payment.paymentDetails': {
    ro: 'Detalii plată',
    en: 'Payment Details',
  },
  'payment.cardDetailsHint': {
    ro: 'Introdu detaliile cardului mai jos. Modul de test acceptă orice card de test Stripe.',
    en: 'Enter your card details below. Test mode accepts any Stripe test card.',
  },
  'payment.intentLoading': {
    ro: 'Se pregătește sesiunea de plată securizată...',
    en: 'Preparing secure payment session...',
  },
  'payment.retryIntent': {
    ro: 'Încearcă din nou',
    en: 'Try again',
  },
  'payment.freePlanTitle': {
    ro: 'Nu este necesară plată',
    en: 'No payment required',
  },
  'payment.freePlanDescription': {
    ro: 'Poți activa planul Gratuit instant. Confirmă mai jos pentru a finaliza înregistrarea.',
    en: 'You can activate the Free plan instantly. Confirm below to finish registration.',
  },
  'payment.freePlanMessage': {
    ro: 'Poți activa planul Gratuit instant. Confirmă mai jos pentru a finaliza înregistrarea.',
    en: 'You can activate the Free plan instantly. Confirm below to finish registration.',
  },
  'payment.orderSummary': {
    ro: 'Rezumat comandă',
    en: 'Order Summary',
  },
  'payment.plan': {
    ro: 'Plan',
    en: 'Plan',
  },
  'payment.price': {
    ro: 'Preț',
    en: 'Price',
  },
  'payment.features': {
    ro: 'Funcționalități',
    en: 'Features',
  },
  'payment.processing': {
    ro: 'Se procesează...',
    en: 'Processing...',
  },
  'payment.completeTest': {
    ro: 'Finalizează plata de test',
    en: 'Complete test payment',
  },
  'payment.securePayment': {
    ro: 'Plată securizată prin Stripe',
    en: 'Secure payment powered by Stripe',
  },
  'payment.testMode': {
    ro: 'Mod test - Fără taxe reale',
    en: 'Test mode - No real charges',
  },
  'auth.step5.title': {
    ro: 'Alege planul tău',
    en: 'Choose Your Plan',
  },
  'auth.step5.subtitle': {
    ro: 'Selectează planul care se potrivește cel mai bine nevoilor tale de nuntă',
    en: 'Select the plan that best fits your wedding needs',
  },
  'auth.step5.selectedPlan': {
    ro: 'Ai selectat planul {{plan}}',
    en: 'You selected the {{plan}} plan',
  },
  'auth.step5.afterStep': {
    ro: 'După acest pas poți completa plata și debloca funcționalitățile.',
    en: 'After this step you can complete the payment and unlock your features.',
  },
  'auth.step5.signingUp': {
    ro: 'Te înregistrezi pentru planul {{plan}}',
    en: 'You\'re signing up for the {{plan}} plan',
  },
  'auth.step5.confirmPayment': {
    ro: 'După acest pas poți confirma plata pentru a debloca funcționalitățile premium.',
    en: 'After this step you can confirm payment to unlock premium features.',
  },
};

/**
 * Get locale-aware fallback for a translation key
 * @param key - Translation key
 * @param locale - Current locale ('ro' | 'en')
 * @returns Fallback string matching the locale, or the key if not found
 */
export function getPricingFallback(key: string, locale: Locale): string {
  const fallback = fallbacks[key];
  if (!fallback) {
    return key; // Return key as last resort
  }
  return fallback[locale] || fallback.ro; // Default to RO if locale not found
}

/**
 * Pricing i18n helper object
 */
export const pricingI18n = {
  get: getPricingFallback,
};

